import React from 'react';
import { createPortal } from 'react-dom';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

interface LoadingProps {
  /**
   * The size of the loading spinner
   * @default 'default'
   */
  size?: 'small' | 'default' | 'large';

  /**
   * Custom color for the loading spinner
   * @default '#743BFF' (Primary purple color)
   */
  color?: string;

  /**
   * Whether to show a background overlay
   * @default false
   */
  fullScreen?: boolean;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom tip to show below the spinner
   */
  tip?: string;

  /**
   * Background opacity for the full-screen overlay
   * @default 0.0
   */
  overlayOpacity?: number;
}

/**
 * Common loading component that can be used throughout the application
 * When fullScreen=true, it renders directly to document.body using a portal
 */
const Loading: React.FC<LoadingProps> = ({
  size = 'default',
  color = '#743BFF',
  fullScreen = false,
  className = '',
  tip = 'ロード中...',
  overlayOpacity = 0.2,
}) => {
  // Map size to appropriate pixel value
  const sizeMap = {
    small: 24,
    default: 40,
    large: 64,
  };

  // Create loading icon with custom color and size
  const loadingIcon = (
    <LoadingOutlined style={{ fontSize: sizeMap[size], color }} spin />
  );

  // Full-screen loader content
  const fullScreenLoader = (
    <div
      className={`
        fixed inset-0 flex items-center justify-center   z-[9999] 
        transition-all duration-300 ease-in-out
      `}
      style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }}
    >
      <div
        className={`
          text-center p-8 rounded-lg animate-fadeIn
          ${className}
        `}
      >
        {tip ? (
          <div className="flex flex-col items-center">
            <Spin indicator={loadingIcon} />
            <div className="mt-4 text-white font-medium text-base">{tip}</div>
          </div>
        ) : (
          <Spin indicator={loadingIcon} />
        )}
      </div>
    </div>
  );

  // If fullScreen, use portal to render directly to document.body
  if (fullScreen) {
    return createPortal(fullScreenLoader, document.body);
  }

  // Regular loading spinner
  return (
    <div className={`flex items-center justify-center ${className}`}>
      {tip ? (
        <div className="flex flex-col items-center">
          <Spin indicator={loadingIcon} />
          <div className="mt-2 font-medium text-sm text-gray-700">{tip}</div>
        </div>
      ) : (
        <Spin indicator={loadingIcon} tip={tip} />
      )}
    </div>
  );
};

export default Loading;
